import React, { useEffect, useState } from 'react';
import { usePaidConfirm } from '../Common/usePaidConfirm';
import { formatApiErrorDetail } from '../../utils/apiErrorDetail';
import {
  CHECK_OUTLINE,
  CHECK_OUTLINE_BUSY,
  CHECK_OUTLINE_CLEAR,
  CHECK_OUTLINE_FAILED,
  CHECK_OUTLINE_HARD,
  CHECK_OUTLINE_HINT,
  CHECK_OUTLINE_SOFT,
  CHECK_OUTLINE_THIN,
  CHECK_OUTLINE_WHERE,
} from '../../utils/writerCopy';

export function checkOutlineErrorCopy(err) {
  const raw = formatApiErrorDetail(err?.detail)
    || formatApiErrorDetail(err?.message)
    || formatApiErrorDetail(err);
  const cleaned = (raw || '').replace(/^VALIDATION:\s*[^-]+-\s*/, '');
  if (!cleaned || /internal error/i.test(cleaned) || /\[object Object\]/i.test(cleaned)) {
    return CHECK_OUTLINE_FAILED;
  }
  return cleaned;
}

function severityLabel(severity) {
  return severity === 'soft' ? CHECK_OUTLINE_SOFT : CHECK_OUTLINE_HARD;
}

/**
 * Peer to Generate. Runs only when the writer clicks.
 * Does not call on first paint, Generate, Accept, or Resume.
 */
export default function PlotTrackCheckOutline({ storyId, checkOutline }) {
  const [busy, setBusy] = useState(false);
  const { ask: askPaid, dialog: paidDialog } = usePaidConfirm();
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    setBusy(false);
    setError(null);
    setResult(null);
  }, [storyId]);

  const onClick = async () => {
    if (!checkOutline || busy) return;
    const ok = await askPaid('check_outline');
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      const next = await checkOutline();
      setResult(next || null);
    } catch (err) {
      setResult(null);
      setError(checkOutlineErrorCopy(err));
    } finally {
      setBusy(false);
    }
  };

  const findings = result?.findings || [];
  const emptyMessage = !findings.length
    ? (result?.message || (result?.status === 'thin' ? CHECK_OUTLINE_THIN : null)
      || (result?.status === 'completed' ? CHECK_OUTLINE_CLEAR : null))
    : null;

  return (
    <>
      {paidDialog}
      <button
        type="button"
        className="pt-generate__btn pt-check__btn"
        onClick={onClick}
        disabled={busy || !checkOutline}
        aria-label={busy ? CHECK_OUTLINE_BUSY : CHECK_OUTLINE}
      >
        {busy ? CHECK_OUTLINE_BUSY : CHECK_OUTLINE}
      </button>
      {(busy || error || result) && (
        <div className="pt-check" role="region" aria-label="Outline check" aria-live="polite">
          {busy && <p className="pt-check__status">{CHECK_OUTLINE_BUSY}</p>}
          {!busy && error && <p className="pt-check__status" role="alert">{error}</p>}
          {!busy && !error && emptyMessage && (
            <p className="pt-check__status" role="status">{emptyMessage}</p>
          )}
          {!busy && !error && findings.length > 0 && (
            <ul className="pt-check__list" aria-label="Findings">
              {findings.map((item, index) => (
                <li className="pt-check__item" key={`${item.pointer_label}-${item.dimension || index}`}>
                  <p className="pt-check__severity">{severityLabel(item.severity)}</p>
                  <p className="pt-check__finding">{item.finding}</p>
                  <p className="pt-check__meta">{CHECK_OUTLINE_WHERE}: {item.pointer_label}</p>
                  <p className="pt-check__meta">{CHECK_OUTLINE_HINT}: {item.hint}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
}
